import {Button, Card, Col, Row, Select, Table, Tag} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import {Loading} from "../../components/Loading/Loading"
import {limitOptionsSelect} from "../../helps/optionsSelect"
import {useHistory, useLocation} from "react-router-dom"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {getAttributesVersions, setAttributesVersions, setAttributesVersionsFilter, setWSAttributesVersion} from "../../store/attributesVersionReducer"
import moment from "moment"
import {FormSelect} from "../../components/Form/FormSelect"

const columns = [
    {
        title: 'Версия',
        dataIndex: 'version',
        key: 'version',
        align: center,
    },
    {
        title: 'Название',
        dataIndex: 'name',
        key: 'name',
        align: center,
    },
    {
        title: 'Файл прошивки',
        dataIndex: 'firmware',
        key: 'firmware',
        align: center,
        render: (firmware: string) => {
            return <Tag color={firmware ? 'green' : 'volcano'} style={{marginRight: 0}}>{firmware ? 'Да' : 'Нет'}</Tag>
        }
    },
    {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        render: (i: any) => {
            return (
                <div className="table-cell-status">
                    <Tag color={i.active ? 'green' : 'volcano'} style={{marginRight: 0}}>{i.active ? 'Активно' : 'Не активно'}</Tag>
                    {i.active ? <Tag color='default' style={{marginRight: 0}}>{moment(i.date).format('DD.MM.YYYY HH:mm')}</Tag>: null}
                </div>
            )
        },
        align: center,
    },
]

export const AttributesVersions = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const versions = useSelector((state: StateType) => state.attributesVersionReducer.versions)
    const filter = useSelector((state: StateType) => state.attributesVersionReducer.filter)
    const ws = useSelector((state: StateType) => state.attributesVersionReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setAttributesVersionsFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getAttributesVersionsFunc = useCallback((withoutLoadingIndicator = false) => {
        if (location?.search) {
            dispatch(getAttributesVersions({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() => {
        getAttributesVersionsFunc()

        return () => {
            dispatch(setAttributesVersions(null))
        }
    }, [dispatch, getAttributesVersionsFunc])

    useEffect(() => {
        return () => {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() => {
        if (ws) {
            getAttributesVersionsFunc(true)
        }
        dispatch(setWSAttributesVersion(null))
    }, [dispatch, ws, getAttributesVersionsFunc])

    const data = useMemo(() => versions?.data?.map(i => {
        return {
            key: i.version,
            id: i.version,
            version: i.version,
            name: i.name,
            firmware: i.firmware,
            status: {
                active: i.active,
                date: i.updatedAt
            },
        }
    }), [versions])

    return (
        <div className="tabled">
            <Helmet>
                <title>Версии</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div className="table-title">
                                  <div>Версии</div>
                                  <Button onClick={() => history.push('/attributesVersionForm')} type="link" size="middle" style={{marginTop: 5}}>
                                      Добавить
                                  </Button>
                              </div>
                          }
                          extra={
                              <div className="table-title-extra">
                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: 'auto'}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                versions
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter?.limit ? {
                                                 position: versions?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: versions.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) => {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id}) => ({
                                                 onClick: () => history.push(`/attributesVersion/${id}`),
                                                 onMouseDown: (e) => e.button === 1 && window.open(`/attributesVersion/${id}`, '_blank', 'noopener,noreferrer')
                                             })}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
